package de.xtl.retroguide.question

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ElevatedCard
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import de.xtl.retroguide.plainText
import de.xtl.retroguide.protocol.AnswerCluster
import de.xtl.retroguide.protocol.VisibleAnswer
import org.jetbrains.compose.resources.stringResource
import retroguide.app.shared.generated.resources.Res
import retroguide.app.shared.generated.resources.cluster_create
import retroguide.app.shared.generated.resources.cluster_delete
import retroguide.app.shared.generated.resources.cluster_name_label
import retroguide.app.shared.generated.resources.cluster_selection_hint
import retroguide.app.shared.generated.resources.clusters_title

/** Named topics with their answers; the moderator can dissolve them via [onDelete]. */
@Composable
internal fun ClusterSection(
    clusters: List<AnswerCluster>,
    answers: List<VisibleAnswer>,
    onDelete: ((String) -> Unit)? = null,
) {
    if (clusters.isEmpty()) return
    val answersById = answers.associateBy { it.id }
    Text(stringResource(Res.string.clusters_title), style = MaterialTheme.typography.titleMedium)
    clusters.forEach { cluster ->
        ElevatedCard(Modifier.fillMaxWidth()) {
            Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(cluster.name, modifier = Modifier.weight(1f), style = MaterialTheme.typography.titleLarge)
                    Surface(shape = RoundedCornerShape(50), color = MaterialTheme.colorScheme.primary) {
                        Text(
                            cluster.answerIds.size.toString(),
                            modifier = Modifier.padding(horizontal = 10.dp, vertical = 2.dp),
                            style = MaterialTheme.typography.labelLarge,
                            color = MaterialTheme.colorScheme.onPrimary,
                        )
                    }
                }
                cluster.answerIds.mapNotNull(answersById::get).forEach { answer ->
                    Text(
                        "• ${answer.plainText} (${answer.participantName})",
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
                onDelete?.let { delete ->
                    TextButton(onClick = { delete(cluster.id) }, modifier = Modifier.align(Alignment.End)) {
                        Text(stringResource(Res.string.cluster_delete))
                    }
                }
            }
        }
    }
}

@Composable
internal fun ClusterComposer(
    selectionCount: Int,
    name: String,
    enabled: Boolean,
    onNameChange: (String) -> Unit,
    onCreate: () -> Unit,
) {
    OutlinedCard(Modifier.fillMaxWidth()) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Text(
                stringResource(Res.string.cluster_selection_hint, selectionCount),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            OutlinedTextField(
                value = name,
                onValueChange = onNameChange,
                label = { Text(stringResource(Res.string.cluster_name_label)) },
                singleLine = true,
                enabled = enabled,
                modifier = Modifier.fillMaxWidth(),
            )
            Button(
                onClick = onCreate,
                enabled = enabled && selectionCount > 0 && name.isNotBlank(),
                modifier = Modifier.fillMaxWidth(),
            ) {
                Text(stringResource(Res.string.cluster_create))
            }
        }
    }
}
